<template>
  <div class="register-form">
    <!-- <logo class="register-form__logo" /> -->

    <div v-if="state.step === 'register'">
      <form ref="form" class="register-form__form" @submit.prevent="submitRegistration">
        <div class="register-form__intro">
          {{ $t("texts.noAccountYet") }}
          <strong>{{ $t("texts.registerForFree") }}</strong>
        </div>

        <input-text
          v-model:value="data.emailValue"
          :label="$t('labels.email')"
          type="email"
          :required="true"
          :error="fieldErrors.email"
          :autofocus="true"
          :hide-errors="state.hideErrors"
        />

        <input-text
          v-model:value="data.firstname"
          :label="$t('labels.firstname')"
          :required="true"
          :error="fieldErrors.firstname"
          :hide-errors="state.hideErrors"
        />

        <input-text
          v-model:value="data.lastname"
          :label="$t('labels.lastname')"
          :required="true"
          :error="fieldErrors.lastname"
          :hide-errors="state.hideErrors"
        />

        <div class="register-form__checkboxes">
          <input-checkbox
            v-model:value="data.acceptedTerms"
            class="mt-4"
            :required="true"
          >
            <template #label>
              <i18n-t keypath="labels.acceptTermsAndConditions" scope="global">
                <template #agb>
                  <a :href="links.agb" target="_blank">{{
                    $t("labels.conditions")
                  }}</a>
                </template>
                <template #terms>
                  <a :href="links.terms" target="_blank">{{
                    $t("labels.termsOfUse")
                  }}</a>
                </template>
              </i18n-t>
            </template>
          </input-checkbox>
          <input-checkbox
            v-model:value="data.acceptedPrivacy"
            :required="true"
            class="mt-2"
          >
            <template #label>
              <i18n-t keypath="labels.readPrivacyNotice" scope="global">
                <template #privacyNotice>
                  <a :href="links.privacy" target="_blank">{{
                    $t("labels.privacyPolicy")
                  }}</a>
                </template>
              </i18n-t>
            </template>
          </input-checkbox>
        </div>

        <div v-show="state.error" class="mt-4 message-error p-4">
          <lucide-icon name="triangle-alert" />
          <p>{{ state.error }}</p>
        </div>
        <div v-show="state.notice" class="mt-4 message-notice p-4">
          <lucide-icon name="circle-alert" />
          <p>{{ state.notice }}</p>
        </div>

        <div class="ta-center">
          <button
            class="button mt-4"
            type="submit"
            :disabled="state.registering"
            @click="state.hideErrors = false"
          >
            <template v-if="!state.registering">
              {{ $t("labels.register") }}
            </template>
            <template v-else>
              {{ $t("labels.registering") }}
            </template>
          </button>
        </div>

        <div class="ta-center mt-4" v-if="$slots.footer">
          <slot name="footer"></slot>
        </div>
      </form>
    </div>

    <form
      v-if="state.step == 'confirm-email'"
      class="register-form__form text-center"
      @submit.prevent="checkVerificationCode()"
    >
      <lucide-icon name="mail" class="self-center mt-2" style="font-size: 32px" />
      <h2 class="title text-center mt-0">{{ $t("headings.confirmEmail") }}</h2>
      <p class="mt-0">
        {{ $t("texts.verifyEmailToCompleteRegistration") }}
      </p>
      <input-text
        v-model:value="verification.code"
        :label="$t('labels.verificationCode')"
        :input-class="['register-form__verification-code']"
        :error="verification.error?.fields?.code"
        pattern="[0-9]+"
        :minlength="6"
        :maxlength="6"
        :hide-errors="!verification.codeSent"
        :autofocus="true"
        @update:value="autoCheckVerificationCode"
      />
      <div v-if="verification.error && !verification.error.fields?.code" class="mt-2 message-error p-4 text-left">
        <lucide-icon name="triangle-alert" />
        <p>{{ verification.error.message }}</p>
      </div>

      <button
        type="submit"
        class="button self-center mt-2"
        :disabled="verification.checking"
        @click="checkVerificationCode"
        >
        {{ $t("actions.confirmEmail") }}
      </button>
      <button
        v-if="verification.error?.fields?.code == 'verification_code_expired'"
        class="button button_secondary mt-2"
        type="button"
        @click="resendVerificationCode"
      >
        {{ $t("actions.resendVerificationCode") }}
      </button>
    </form>

    <form
      v-if="state.step === 'password'"
      class="register-form__form"
      @submit.prevent="setPassword"
    >
      <lucide-icon name="key-round" class="self-center mt-2" style="font-size: 32px" />
      <h2 class="title text-center">{{ $t("headings.createPassword") }}</h2>
      <p class="mt-0 text-center">
        {{ $t("texts.onboardingPassword") }}
      </p>
      <input type="text" hidden name="email" autocomplete="username" :value="data.emailValue" />
      <input-password
        v-model:value="passwordData.password"
        :label="$t('labels.password')"
        autocomplete="new-password"
        :required="true"
        :autofocus="true"
        :strength-visible="true"
      />
      <small>{{ $t("texts.passwordRequirements") }}</small>
      <h4 class="mb-0 label">{{ $t("headings.recommendations") }}</h4>
      <ul class="mt-1 register-form__passwordstatus-list">
        <li>
          <span class="register-form__passwordstatus">
            <lucide-icon v-if="passwordLongEnough" name="check" />
            <lucide-icon v-else name="x" />
          </span>
          {{ $t("passwordRecommendations.length") }}
        </li>
        <li>
          <span class="register-form__passwordstatus">
            <lucide-icon v-if="passwordHasUppercase" name="check" />
            <lucide-icon v-else name="x" />
          </span>
          {{ $t("passwordRecommendations.uppercase") }}
        </li>
        <li>
          <span class="register-form__passwordstatus">
            <lucide-icon v-if="passwordHasLowercase" name="check" />
            <lucide-icon v-else name="x" />
          </span>
          {{ $t("passwordRecommendations.lowercase") }}
        </li>
        <li>
          <span class="register-form__passwordstatus">
            <lucide-icon v-if="passwordHasNumber" name="check" />
            <lucide-icon v-else name="x" />
          </span>
          {{ $t("passwordRecommendations.number") }}
        </li>
        <li>
          <span class="register-form__passwordstatus">
            <lucide-icon v-if="passwordHasSpecial" name="check" />
            <lucide-icon v-else name="x" />
          </span>
          {{ $t("passwordRecommendations.special") }}
        </li>
      </ul>
      <!-- 
      @update:value="checkPasswordConfirmError()"
      <input-password
        v-model:value="passwordData.passwordConfirm"
        :label="$t('labels.passwordRepeat')"
        autocomplete="new-password"
        :required="true"
        :error="passwordData.passwordConfirmError"
        @update:value="checkPasswordConfirmError()"
      /> -->
      
      <div v-if="passwordData.errorMessage" class="message-error mt-4">
        {{ passwordData.errorMessage }}
      </div>

      <button class="button mt-4" type="submit" :disabled="passwordData.password.length < 5">
        {{ $t("actions.setPassword") }}
      </button>
    </form>

    <section v-if="state.step == 'success'" class="text-center">
      <lucide-icon name="party-popper" class="self-center mt-2" style="font-size: 32px" />
      <h2 class="title text-center">{{ $t("headings.welcomeToSimcision") }}</h2>
    </section>

    <slot name="after" :step="state.step"></slot>
  </div>
</template>

<script setup lang="ts">
import { reactive, watch, computed } from "vue";
import { post, saveToken } from "@/api/API";
import { useRoute } from "vue-router";
import { useI18n } from "vue-i18n";
import { wait } from "@/helpers/utility/Timing";

import LucideIcon from "@core/ui/LucideIcon.vue";
// import Logo from "@core/ui/SimcisionLogo.vue";
import InputText from "@core/input/InputText.vue";
import InputPassword from "@core/input/InputPassword.vue";
import InputCheckbox from "@core/input/InputCheckbox.vue";

import useTermsLinks from "../common/useTermsLinks";
import { BadRequestError } from "@core/errors/Errors";
import { useUser } from "@/api/user.store";
import eventHub from "@/helpers/utility/Events";
import { showMessage } from "@core/ui/alert/alert";

import type { User } from "@/api/User";
import type { AuthData } from "@/api/API";

const props = defineProps<{
  email?: string;
}>();
const emit = defineEmits<{
  (e: "update:email", value: string): void;
  (e: "update:step", step: "register" | "confirm-email" | "password"): void;
  (e: "register", user: User): void;
}>();

const route = useRoute();
const { links } = useTermsLinks();
const { updateUser } = useUser();
const { t } = useI18n();

const data = reactive({
  firstname: "",
  lastname: "",
  emailValue: props.email || "",
  acceptedTerms: false,
  acceptedPrivacy: false,
  userId: null as null | number
});

const state = reactive({
  error: "",
  notice: "",
  registering: false,
  hideErrors: true,
  step: "register"
});

const fieldErrors = reactive({
  firstname: undefined as undefined | string,
  lastname: undefined as undefined | string,
  email: undefined as undefined | string
});

function setStep(step: "register" | "confirm-email" | "password") {
  state.step = step;
  emit("update:step", step);
}

watch(
  () => props.email,
  (value) => {
    data.emailValue = value || "";
  },
  { immediate: true }
);

watch(
  () => data.emailValue,
  (value) => {
    if (props.email !== value) {
      emit("update:email", value);
    }
  }
);

async function submitRegistration() {
  resetFieldErrors();
  try {
    state.registering = true;
    const { id } = await post<{ id: number }>("/register/", {
      sendAuthToken: false,
      data: {
        email: data.emailValue.trim(),
        firstname: data.firstname.trim(),
        lastname: data.lastname.trim(),
      }
    });
    console.log("registered user", id);
    data.userId = id;
    setStep("confirm-email");
  } catch (_error) {
    const error = _error as Error | BadRequestError;
    if ("fields" in error && error.fields) {
      if (error.fields.email) {
        state.notice = t("errors.alreadyRegistered");
      }
      for (const key in error.fields) {
        fieldErrors[key as keyof typeof fieldErrors] = error.fields[key];
      }
    } else {
      state.error = error.message;
      console.log(state.error, error, error.message);
    }
  } finally {
    state.registering = false;
  }
}

function resetFieldErrors() {
  state.error = "";
  fieldErrors.firstname = undefined;
  fieldErrors.lastname = undefined;
  fieldErrors.email = undefined;
}

// ==================================
// VERIFICATION CODE

const verification = reactive({
  code: "",
  error: null as null | Error & { fields?: { code: string } },
  checking: false,
  codeSent: false
});

watch(() => route.query, (query) => {
  if (query.code && query.userId) {
    const base64UserId = query.userId as string;
    const decodedUserId = atob(base64UserId);
    setStep("confirm-email");
    data.userId = parseInt(decodedUserId, 10);
    verification.code = query.code as string;
    console.log("userId", base64UserId, decodedUserId, data.userId);
    autoCheckVerificationCode();
  }
}, { immediate: true, deep: true });

async function autoCheckVerificationCode() {
  const code = verification.code.trim();
  verification.error = null;
  if (code.length === 6) {
    verification.checking = true;
    await wait(200);
    await checkVerificationCode();
  }
}

async function checkVerificationCode() {
  verification.error = null;
  verification.checking = true;
  const code = verification.code.trim();
  verification.codeSent = true;
  try {
    // verification returns the user object if successful
    // also contains the password reset token?
    const authData = await post<AuthData>("/register/verify/", {
      sendAuthToken: false,
      data: {
        id: data.userId,
        code
      }
    });
    saveToken(authData);
    // setUser(user);
    // console.log("confirmed", user);
    state.step = "password";
  } catch (e) {
    const error = e as Error & { fields?: { code: string } };
    verification.error = error;
  }
  verification.checking = false;
}

async function resendVerificationCode() {
  verification.error = null;
  verification.checking = true;
  try {
    await post("/register/resend-verification/", {
      sendAuthToken: false,
      data: {
        id: data.userId
      }
    });
    showMessage(t("messages.verificationCodeResent"));
  } catch (e) {
    const error = e as Error;
    state.error = error.message;
  }
  verification.checking = false;
}

// ==================================
// PASSWORD

const passwordData = reactive({
  password: "",
  // passwordConfirm: "",
  errorMessage: "",
  passwordError: "",
  passwordConfirmError: ""
});

async function setPassword() {
  passwordData.errorMessage = "";
  passwordData.passwordError = "";
  passwordData.passwordConfirmError = "";

  // if (passwordData.password !== passwordData.passwordConfirm) {
  //   passwordData.passwordConfirmError = t("errors.passwordsDoNotMatch");
  //   return;
  // }

  try {
    const user = await updateUser({
      password: passwordData.password,
      acceptTerms: true
    });
    state.step = "success";
    await wait(2000);
    eventHub.emit("login", user);
    emit("register", user);
  } catch (e) {
    const error = e as Error;
    if (error.message) {
      passwordData.errorMessage = error.message;
    } else {
      console.log(error);
    }
  }
}

const passwordLongEnough = computed(() => passwordData.password.length >= 12);
const passwordHasUppercase = computed(() => /[A-Z]/.test(passwordData.password));
const passwordHasLowercase = computed(() => /[a-z]/.test(passwordData.password));
const passwordHasNumber = computed(() => /[0-9]/.test(passwordData.password));
const passwordHasSpecial = computed(() => /[^0-9a-zA-Z]+/.test(passwordData.password));

// function checkPasswordConfirmError() {
//   if (!passwordData.passwordConfirmError) return;
//   if (passwordData.password === passwordData.passwordConfirm) {
//     passwordData.passwordConfirmError = "";
//   }
// }
</script>

<style lang="scss">
.register-form {
  min-width: 180px;

  &__heading {
    text-align: center;
  }

  &__intro {
    text-align: center;
    font-size: 18px;
    // margin-top: 0.5rem;
    margin-bottom: 1.5rem;
    text-wrap: balance;
    line-height: 1.4;
  }

  &__form {
    display: flex;
    gap: 0.5rem;
    flex-direction: column;
    max-width: 30rem;
  }

  &__logo {
    display: block;
    margin: 0 auto;
    margin-bottom: 1rem;
  }

  &__line {
    display: flex;
    gap: 1rem;
  }

  &__checkboxes {
    margin-top: 1.25rem;
    margin-bottom: 1rem;
  }

  &__verification-code {
    text-align: center;
    font-size: 1.5rem;
  }

  &__confirmation {
    text-align: center;
  }

  &__passwordstatus-list {
    display: flex;
    flex-direction: column;
    gap: 0.5rem;
    margin-top: 0.5rem;
    list-style: none;
    padding-left: 1em;
  }
  &__passwordstatus {
    color: var(--color-text-medium);
    position: relative;
    bottom: -1px;
    .icon-check {
      color: var(--color-success-dark);
    }
  }
}
</style>
